import { CustomerAddress } from "../types";
import { getCustomerFullName } from "../utils/customerUtils";
import { apiService } from "./api";

/**
 * Order summaries to Telegram. Sending goes through our API, which holds the
 * bot token and looks up the chat — the token used to be built into this
 * app's public JavaScript. Formatting stays here.
 */
class TelegramService {
    /**
     * Post `text` (Telegram HTML) to the order group, or to a courier's own
     * chat when `deliveryServiceId` is given. Throws with the server's reason.
     */
    private async send(text: string, deliveryServiceId?: string): Promise<void> {
        try {
            await apiService.post(
                "/telegram/send",
                { text, delivery_service_id: deliveryServiceId ?? null },
                // The server gives Telegram up to 15 s.
                { timeout: 25000 },
            );
        } catch (error: any) {
            const detail = error?.response?.data?.detail;
            throw new Error(
                typeof detail === "string"
                    ? detail
                    : error?.message || "Failed to send to Telegram",
            );
        }
    }

    /**
     * Format order details for Telegram message
     */
    formatOrderMessage(order: any): string {
        // Check if this is a customer order (has order_number) or purchase order (has purchase_number)
        const isCustomerOrder = order.order_number && !order.purchase_number;

        if (isCustomerOrder) {
            return this.formatCustomerOrderMessage(order);
        } else {
            return this.formatPurchaseOrderMessage(order);
        }
    }

    /**
     * Same content as formatOrderMessage, without the Telegram-specific
     * <b>/<a> markup — for anywhere that isn't Telegram's HTML parse mode
     * (e.g. pasting into WhatsApp or Notes), where those tags would show
     * up as literal text instead of being rendered.
     */
    formatOrderMessagePlainText(order: any): string {
        return this.formatOrderMessage(order)
            // <a href="URL">text</a> -> "text (URL)" so the link survives
            // as visible plain text instead of just disappearing.
            .replace(/<a href="([^"]*)">([^<]*)<\/a>/g, "$2 ($1)")
            // Everything else is purely visual (currently just <b>/</b>).
            .replace(/<[^>]+>/g, "");
    }

    /**
     * Format customer order message
     */
    formatCustomerOrderMessage(order: any): string {
        const items = order.items || [];

        let message = `👤 `;
        if (order.customer) {
            message += `${getCustomerFullName(order.customer)}\n`;
            if (order.customer.email) {
                message += `${order.customer.email}\n`;
            }
            if (order.customer.phone) {
                message += `${order.customer.phone}\n`;
            }
        } else {
            message += `Walk-in customer\n`;
        }

        // Add shipping address - prioritize order.shipping_address field
        message += `\n<b>📍 Shipping Address:</b>\n`;

        if (order.shipping_address) {
            message += `${order.shipping_address}\n`;
        } else if (
            order.customer &&
            order.customer.addresses &&
            order.customer.addresses.length > 0
        ) {
            // Fallback to customer addresses if order.shipping_address is not available
            const defaultAddress =
                order.customer.addresses.find(
                    (addr: CustomerAddress) =>
                        addr.is_default && addr.address_type === "SHIPPING",
                ) ||
                order.customer.addresses.find(
                    (addr: CustomerAddress) => addr.address_type === "SHIPPING",
                ) ||
                order.customer.addresses.find(
                    (addr: CustomerAddress) => addr.is_default,
                ) ||
                order.customer.addresses[0];

            if (defaultAddress) {
                message += `${defaultAddress.street_address}\n`;
                message += `${defaultAddress.city}\n`;
            }
        }

        // Add map link from customer's default shipping address if available
        if (
            order.customer &&
            order.customer.addresses &&
            order.customer.addresses.length > 0
        ) {
            const shippingAddress =
                order.customer.addresses.find(
                    (addr: CustomerAddress) =>
                        addr.is_default && addr.address_type === "SHIPPING",
                ) ||
                order.customer.addresses.find(
                    (addr: CustomerAddress) => addr.address_type === "SHIPPING",
                ) ||
                order.customer.addresses.find(
                    (addr: CustomerAddress) => addr.is_default,
                ) ||
                order.customer.addresses[0];

            if (shippingAddress && shippingAddress.map_link) {
                message += `<a href="${shippingAddress.map_link}">📍 View on Map</a>\n`;
            }
        }

        message += `\n<b>📦 Products (${items.length}):</b>\n`;

        // Products breakdown with detailed information
        items.forEach((item: any, index: number) => {
            const product = item.product || {};
            const unitPrice = item.unit_price || item.price || 0;
            const totalPrice = item.total_price || item.quantity * unitPrice;

            message += `\n${index + 1}. <b>${
                product.name || "Unknown Product"
            }</b> x ${item.quantity} = ৳${unitPrice} x ${
                item.quantity
            } = ৳${totalPrice}\n`;
        });

        message += `\n<b>💰 Summary:</b>\n`;
        message += `Subtotal: ৳${
            order.items_total ||
            order.total_amount -
                (order.shipping_amount || 0) +
                (order.discount_amount || 0)
        }\n`;

        if (order.shipping_amount && order.shipping_amount > 0) {
            message += `Shipping: ৳${order.shipping_amount}\n`;
        }

        if (order.discount_amount && order.discount_amount > 0) {
            message += `Discount: -৳${order.discount_amount}\n`;
        }

        message += `<b>Total: ৳${order.total_amount}</b>\n`;

        // Payment status — critical for COD deliveries, so the courier knows
        // whether anything is still owed on delivery.
        const amountPaid =
            order.amount_paid ??
            (order.payment_transactions || []).reduce(
                (sum: number, p: any) => sum + Number(p.amount || 0),
                0,
            );
        const amountDue =
            order.amount_due ??
            Math.max(0, Number(order.total_amount || 0) - amountPaid);

        message += `\n<b>💳 Payment (${order.payment_status || "UNPAID"}):</b>\n`;
        message += `Paid: ৳${amountPaid}\n`;
        if (amountDue > 0) {
            message += `<b>Due: ৳${amountDue}</b>\n`;
        }

        if (order.payment_method) {
            message += `Payment Method: ${order.payment_method}\n`;
        }

        if (order.notes) {
            message += `\n<b>📝 Notes:</b>\n${order.notes}`;
        }

        return message;
    }

    /**
     * Format purchase order message
     */
    formatPurchaseOrderMessage(order: any): string {
        const items = order.items || [];
        const supplier = order.supplier || {};

        let message = `🛒 <b>New Purchase Order</b>\n\n`;

        message += `<b>📋 Order Details:</b>\n`;
        message += `PO Number: <code>${order.purchase_number}</code>\n`;
        message += `Status: ${order.status}\n`;
        message += `Date: ${new Date(
            order.purchase_date,
        ).toLocaleDateString()}\n`;

        if (order.expected_delivery_date) {
            message += `Expected Delivery: ${new Date(
                order.expected_delivery_date,
            ).toLocaleDateString()}\n`;
        }

        if (order.reference_number) {
            message += `Reference: ${order.reference_number}\n`;
        }

        message += `\n<b>🏢 Supplier:</b>\n`;
        message += `${supplier.name}\n`;
        message += `${supplier.contact_person} - ${supplier.email}\n`;

        message += `\n<b>📦 Items (${items.length}):</b>\n`;

        items.forEach((item: any, index: number) => {
            const product = item.product || {};
            message += `\n${index + 1}. <b>${
                product.name || "Unknown Product"
            }</b>\n`;
            message += `   SKU: ${product.sku || "N/A"}\n`;
            message += `   Quantity: ${item.quantity_ordered}\n`;
            message += `   Unit Cost: ৳${item.unit_cost}\n`;
            message += `   Total: ৳${item.total_cost}\n`;

            if (item.quality_check_required) {
                message += `   ⚠️ Quality Check Required\n`;
            }

            if (item.notes) {
                message += `   Notes: ${item.notes}\n`;
            }
        });

        message += `\n<b>💰 Financial Summary:</b>\n`;
        message += `Subtotal: ৳${
            order.total_amount - (order.shipping_cost || 0)
        }\n`;

        if (order.shipping_cost && order.shipping_cost > 0) {
            message += `Shipping: ৳${order.shipping_cost}\n`;
        }

        if (order.discount_amount && order.discount_amount > 0) {
            message += `Discount: -৳${order.discount_amount}\n`;
        }

        message += `<b>Total: ৳${order.total_amount}</b>\n`;

        if (order.payment_terms) {
            message += `Payment Terms: ${order.payment_terms}\n`;
        }

        if (order.notes) {
            message += `\n<b>📝 Notes:</b>\n${order.notes}`;
        }

        return message;
    }

    /** Send the order (or purchase order) summary to the order group. */
    async sendOrderToGroup(order: any): Promise<void> {
        await this.send(this.formatOrderMessage(order));
    }

    /** Send the order summary to a courier's own Telegram chat. */
    async sendOrderToDeliveryService(order: any, deliveryServiceId: string): Promise<void> {
        await this.send(this.formatOrderMessage(order), deliveryServiceId);
    }

    /**
     * Send order notification automatically if conditions are met
     * This is called when:
     * 1. Order is created with status other than PENDING
     * 2. Order status changes from PENDING to CONFIRMED or higher
     */
    async sendOrderNotificationIfNeeded(
        order: any,
        oldStatus?: string,
    ): Promise<void> {
        try {
            const currentStatus = order.status;
            const shouldSendNotification =
                // Case 1: Order created with non-PENDING status
                (!oldStatus && currentStatus !== "PENDING") ||
                // Case 2: Status changed from PENDING to CONFIRMED or higher
                (oldStatus === "PENDING" &&
                    currentStatus !== "PENDING" &&
                    currentStatus !== "CANCELLED");

            if (shouldSendNotification) {
                console.log(
                    `Sending automatic Telegram notification for order ${order.order_number}`,
                );
                await this.sendOrderToGroup(order);
            }
        } catch (error) {
            // Log error but don't throw - automatic notification failure shouldn't break the flow
            console.error(
                "Failed to send automatic Telegram notification:",
                error,
            );
        }
    }
}

export const telegramService = new TelegramService();
export default telegramService;
